import React, { FunctionComponent, useRef } from 'react';
import { ApolloError } from 'apollo-boost';
import { Container, Header } from 'semantic-ui-react';
import withStyles from 'react-jss';
import {
  isNil,
  values,
  groupBy,
  mapObjIndexed,
  reject,
  sortBy,
  prop,
  isEmpty,
  pathOr,
  path,
} from 'ramda';

import { ClassProp } from 'helpers/types';

import Query from 'components/Query';
import DownloadWidget from 'components/DownloadWidget';
import { ChartPlaceholder } from 'components/Placeholder';
import { useDatePicker } from 'components/DatePicker/DatePicker.context';

import VenueTable from 'scenes/Home/components/VenueTable';
import { Venue } from 'scenes/Home/components/VenueTable/VenueTable';
import { CSVFields } from 'scenes/Home/Home.helpers';

import { GET_MERCHANT_VENUE_SEARCH_RESULTS } from './SearchResults.queries';

const styles = {
  container: {
    margin: '2.25em auto !important',
    height: 'auto !important',
  },
  header: {
    display: 'flex !important',
    paddingTop: '1.875em !important',
  },
};

type QueryResponse = {
  searchMerchantVenues: {
    result: Venue[];
  };
  loading?: boolean;
  error?: ApolloError;
};

type Props = {
  classes: ClassProp<typeof styles>;
  searchType: string;
  searchText: string;
  cnidsToFilter: string;
  filter: string;
};

const renderTable = (
  venues: Venue[],
  contentRef: React.MutableRefObject<null>,
) => (
  <>
    <DownloadWidget
      nodeRef={contentRef}
      imageName={`${path([0, 'venueName'], venues)}_Venues`}
      data={venues}
      fields={CSVFields}
    />

    <div ref={contentRef}>
      <VenueTable data={venues} />
    </div>
  </>
);

const renderVenues = (
  classes: ClassProp<typeof styles>,
  contentRef: React.MutableRefObject<null>,
) => (venues: Venue[], key: any) => (
  <Container key={key} className={classes.container}>
    {renderTable(venues, contentRef)}
  </Container>
);

const isNotValid = (data: Venue[]) => isEmpty(data) || isNil(data);

const groupVenues = groupBy(
  ({ offerNetworkExternalVenueId }: any) => offerNetworkExternalVenueId,
);
const renderNoVenues = (classes: ClassProp<typeof styles>) => (
  <Header as="h3" className={classes.header} content="No Venues found!" />
);

const renderSearchResults = (
  classes: ClassProp<typeof styles>,
  contentRef: React.MutableRefObject<null>,
) => (response: QueryResponse) => {
  const result = pathOr(
    [],
    ['data', 'searchMerchantVenues', 'result'],
    response,
  ) as Venue[];

  if (response.loading) {
    return <ChartPlaceholder />;
  }

  const sorted: any = sortBy(prop('merchantName'));
  const grouped: any = groupVenues(sorted(result));

  if (isNotValid(result)) {
    return renderNoVenues(classes);
  }
  return values(mapObjIndexed(renderVenues(classes, contentRef), grouped));
};

const VenueSearchResults: FunctionComponent<Props> = ({
  classes,
  searchType,
  searchText,
  cnidsToFilter,
  filter,
}) => {
  const { startDate, endDate } = useDatePicker();
  const contentRef = useRef(null);
  const params = reject(isEmpty, {
    startDate,
    endDate,
    pagingInput: {
      offset: 0,
      limit: 10000,
    },
    [searchType]: searchText,
    [filter]: cnidsToFilter,
  });

  return (
    <Query
      enableProgressInfo
      query={GET_MERCHANT_VENUE_SEARCH_RESULTS}
      variables={{
        params,
      }}
      render={renderSearchResults(classes, contentRef)}
    />
  );
};

export default withStyles(styles)(VenueSearchResults);
